import { IStringerBlock } from '@stair-craft/common/util';
import { BufferGeometry, Float32BufferAttribute } from 'three';

/**
 * Real-world size of one wood texture tile (mm); shared by every wood part of the stair. 600 mm on
 * 512 texels is about 1.2 mm a texel: enough for fibres a few millimetres apart (`wood-grain.ts`).
 */
export const WOOD_TILE_MM: number = 600;

interface IPlanPoint {
    x: number;
    z: number;
}

type IHeightAt = (point: IPlanPoint) => number;

/** Signed doubled area in XZ: positive for counter-clockwise when X points right and Z down. */
function signedArea(polygon: readonly IPlanPoint[]): number {
    let area: number = 0;
    for (let i: number = 0; i < polygon.length; i++) {
        const a: IPlanPoint = polygon[i];
        const b: IPlanPoint = polygon[(i + 1) % polygon.length];
        area += a.x * b.z - b.x * a.z;
    }
    return area;
}

/**
 * A vertical prism over a convex plan outline whose top and bottom are given per point (flat or
 * sloped planes). Top face, bottom face, then one quad per outline edge — the same vertex layout
 * the winder tread uses, so the joint checker reads both the same way.
 */
export function buildPlanPrismGeometry(outline: readonly IPlanPoint[], topAt: IHeightAt, bottomAt: IHeightAt): BufferGeometry {
    // Clockwise in XZ gives a +Y normal on the top fan.
    const polygon: IPlanPoint[] = signedArea(outline) > 0 ? [...outline].reverse() : [...outline];
    const count: number = polygon.length;
    const positions: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];

    for (const point of polygon) {
        positions.push(point.x, topAt(point), point.z);
        uvs.push(point.x / WOOD_TILE_MM, point.z / WOOD_TILE_MM);
    }
    for (let i: number = 1; i < count - 1; i++) {
        indices.push(0, i, i + 1);
    }
    for (const point of polygon) {
        positions.push(point.x, bottomAt(point), point.z);
        uvs.push(point.x / WOOD_TILE_MM, point.z / WOOD_TILE_MM);
    }
    for (let i: number = 1; i < count - 1; i++) {
        indices.push(count, count + i + 1, count + i);
    }

    let vertex: number = 2 * count;
    let perimeter: number = 0;
    for (let i: number = 0; i < count; i++) {
        const a: IPlanPoint = polygon[i];
        const b: IPlanPoint = polygon[(i + 1) % count];
        const length: number = Math.hypot(b.x - a.x, b.z - a.z);
        const u0: number = perimeter / WOOD_TILE_MM;
        const u1: number = (perimeter + length) / WOOD_TILE_MM;
        positions.push(a.x, topAt(a), a.z, b.x, topAt(b), b.z, b.x, bottomAt(b), b.z, a.x, bottomAt(a), a.z);
        uvs.push(u0, topAt(a) / WOOD_TILE_MM, u1, topAt(b) / WOOD_TILE_MM, u1, bottomAt(b) / WOOD_TILE_MM, u0, bottomAt(a) / WOOD_TILE_MM);
        // Wound so the side faces outward: seen from outside, a side wound the other way is culled
        // and shows the far wall's inside, and whatever the board houses, through it.
        indices.push(vertex, vertex + 2, vertex + 1, vertex, vertex + 3, vertex + 2);
        vertex += 4;
        perimeter += length;
    }

    const geometry: BufferGeometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
}

/** A turn-zone board segment: its outline with the top and bottom planes of the block. */
export function buildBlockGeometry(block: IStringerBlock): BufferGeometry {
    const dx: number = block.to.x - block.from.x;
    const dz: number = block.to.z - block.from.z;
    const lengthSq: number = Math.max(1e-9, dx * dx + dz * dz);
    const along: IHeightAt = (point: IPlanPoint): number => ((point.x - block.from.x) * dx + (point.z - block.from.z) * dz) / lengthSq;
    return buildPlanPrismGeometry(
        block.outline,
        (point: IPlanPoint): number => block.topFrom + along(point) * (block.topTo - block.topFrom),
        (point: IPlanPoint): number => block.bottomFrom + along(point) * (block.bottomTo - block.bottomFrom)
    );
}
